"use client"

import styled from "@emotion/styled"
import { Box } from "@mui/material"
import React from "react"
import { mediaQueries } from "ui/theme/mediaQueries"
import { colors, sizes } from "ui/theme/theme"

export interface BlockWrapperProps {
  children: React.ReactNode
  bgColor?: string
  alignItems?: string
  style?: React.CSSProperties
  className?: string
  id?: string // Added anchorId prop
}

const bwSizes = sizes.BlockWrapper

// Base styled container with common styles
const StyledContainer = styled(Box)<{ bgcolor?: string }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: hidden;

  ${mediaQueries.mobile} {
    padding: ${bwSizes.mobile.paddingTop}${bwSizes.mobile.paddingLeft}${bwSizes
        .mobile.paddingTop};
    border-top-left-radius: ${sizes.borderRadius};
    border-top-right-radius: ${sizes.borderRadius};
    margin-top: ${bwSizes.mobile.marginTop};
  }

  ${mediaQueries.tablet} {
    padding: ${bwSizes.mobile.paddingTop}${bwSizes.mobile.paddingLeft}${bwSizes
        .mobile.paddingTop};
    border-top-left-radius: ${sizes.borderRadius};
    border-top-right-radius: ${sizes.borderRadius};
    margin-top: ${bwSizes.mobile.marginTop};
  }

  ${mediaQueries.desktopM} {
    border-top-left-radius: ${sizes.borderRadiusTriple};
    border-top-right-radius: ${sizes.borderRadiusTriple};
    min-height: 596px;
    padding: ${bwSizes.desktopM.padding};
    margin-top: ${bwSizes.desktopM.marginTop};
    z-index: 4;
  }

  ${mediaQueries.desktopXl} {
    padding: ${bwSizes.desktop.padding};
  }
`

// Container with custom background color
const StyledContainerCustomBG = styled(StyledContainer)`
  background-color: ${(props) => props.bgcolor};
`

// Container with regular background pattern (alternating colors)
const StyledContainerRegularBG = styled(StyledContainer)`
  background-color: ${(props) =>
    props.bgcolor ? props.bgcolor : colors.beige};

  &:nth-of-type(odd) {
    background-color: ${(props) =>
      props.bgcolor ? props.bgcolor : colors.lightGray};
  }
`

const StyledLimitWidth = styled(Box)`
  width: 100%;
  max-width: ${sizes.maxWidthContainer};
  display: flex;
  flex-direction: column;
  gap: ${sizes.baseSix};
  ${mediaQueries.desktopXl} {
    gap: ${sizes.baseSepta};
  }
`

const BlockWrapper: React.FC<BlockWrapperProps> = ({
  children,
  bgColor,
  style,
  className,
  id,
}) => {
  // Determine which container to use based on whether a custom bgColor was provided
  // Process bgColor to ensure it has a "#" prefix if it's a hex color
  const formattedBgColor =
    bgColor && !bgColor.startsWith("#") ? `#${bgColor}` : bgColor

  // Determine which container to use based on whether a custom bgColor was provided
  const ContainerComponent = formattedBgColor
    ? StyledContainerCustomBG
    : StyledContainerRegularBG

  return (
    <ContainerComponent
      bgcolor={formattedBgColor}
      className={className}
      id={id}
    >
      <StyledLimitWidth className={className} style={style}>
        {children}
      </StyledLimitWidth>
    </ContainerComponent>
  )
}

export default BlockWrapper
